import { useEffect, useMemo, useState } from 'react';

import { readStoredUserSummary } from '../../../infrastructure/session/browserAuthStore';
import {
  ADMIN_TIER_ROLES,
  PERSONAS,
  VALID_PERSONAS
} from '../../user/profile/domain/personas';

import type { WorkspaceNavigation } from '../../../config/workspaceNavigation';

import { useActiveWorkspace } from './useActiveWorkspace';

// Canonical source for "what may this user see in the active workspace".
// Pulls role / persona / visible_sections from the cached me/summary
// response only — every other surface in the codebase (state.seed,
// /workspaces/ list, user.profile.seed_memberships) has been observed to
// drift, and the foundation plan (Track B step B2) collapses those to
// this hook.
//
// The "admin-tier role override" (role in {owner, admin} ⇒ effective
// persona is admin) lives here so it's applied identically wherever
// visibility is decided — previously only useActivePersona ran it, which
// meant other consumers could disagree about whether an owner-with-
// contributor-persona was really an admin.

type PersonaValue = typeof PERSONAS[keyof typeof PERSONAS];

export type WorkspaceVisibility = {
  workspaceId: string | null;
  role: string | null;
  persona: PersonaValue | null;
  effectivePersona: PersonaValue | null;
  visibleSections: string[] | null;
  /**
   * The workspace's own app-shell composition — which nav sections/items, rail
   * cards and header chips it shows. `null` for every workspace nobody
   * configured, which every consumer must treat as "render as you always have".
   *
   * It lives here beside `visibleSections` on purpose: the two are read
   * together everywhere (ceiling, then the workspace's choice beneath it), and
   * splitting them across hooks is how the pre-existing drift between
   * Sidebar.jsx / SeedInfo.jsx / useActiveWorkspace happened.
   */
  navigation: WorkspaceNavigation | null;
  // True when the user's membership on the active workspace is PENDING owner
  // approval (a self-service volunteer/contributor join). The persona
  // dashboard still renders, but behind a full-surface "pending approval"
  // lock until the owner approves.
  isPendingApproval: boolean;
  isLoading: boolean;
};

type WorkspaceSummary = {
  id?: string;
  role?: string | null;
  persona?: string | null;
  visible_sections?: string[] | null;
  navigation?: WorkspaceNavigation | null;
  membership_status?: string | null;
  is_pending_approval?: boolean;
};

type SummaryShape = {
  workspaces?: WorkspaceSummary[];
  data?: { workspaces?: WorkspaceSummary[] };
  active_impersonation?: {
    target_workspace_id?: string;
    target_persona?: string;
    visible_sections?: string[] | null;
  } | null;
};

const readWorkspaces = (summary: SummaryShape | null): WorkspaceSummary[] => {
  if (!summary) return [];
  return summary?.data?.workspaces || summary?.workspaces || [];
};

const readActiveImpersonation = (summary: SummaryShape | null) => {
  if (!summary) return null;
  return (
    (summary as any)?.data?.active_impersonation ||
    summary?.active_impersonation ||
    null
  );
};

const normalizePersona = (
  value: string | null | undefined
): PersonaValue | null => {
  if (!value) return null;
  return VALID_PERSONAS.has(value as PersonaValue)
    ? (value as PersonaValue)
    : null;
};

const resolveVisibility = (
  summary: SummaryShape | null,
  workspaceId: string | null
): Omit<WorkspaceVisibility, 'workspaceId' | 'isLoading'> => {
  const empty = {
    role: null,
    persona: null,
    effectivePersona: null,
    visibleSections: null,
    navigation: null,
    isPendingApproval: false
  };

  if (!summary || !workspaceId) {
    return empty;
  }

  // Active impersonation pins the persona regardless of role — the user
  // is intentionally previewing as someone else. Mirrors
  // useActivePersona's impersonation branch.
  const impersonation = readActiveImpersonation(summary);
  if (
    impersonation &&
    String(impersonation.target_workspace_id) === String(workspaceId)
  ) {
    const persona = normalizePersona(impersonation.target_persona);
    if (persona) {
      return {
        role: null,
        persona,
        effectivePersona: persona,
        // me/summary now derives visible_sections for the impersonated
        // persona so the previewed sidebar is trimmed to what the target
        // would actually see (instead of falling back to full admin).
        visibleSections: Array.isArray(impersonation.visible_sections)
          ? impersonation.visible_sections
          : null,
        // Impersonation previews the TARGET persona's ceiling. The workspace's
        // composition is a property of the workspace, not of who is looking, so
        // it is read from the workspace entry below rather than pinned here —
        // an operator previewing a persona should see the shell that persona
        // really gets, composition included.
        navigation:
          readWorkspaces(summary).find(
            (ws) => String(ws?.id) === String(workspaceId)
          )?.navigation ?? null,
        // Impersonation previews are never themselves "pending approval".
        isPendingApproval: false
      };
    }
  }

  const match = readWorkspaces(summary).find(
    (ws) => String(ws?.id) === String(workspaceId)
  );

  if (!match) return empty;

  const role = (match.role || '').toString().trim().toLowerCase() || null;
  const persona = normalizePersona(match.persona);

  // Backend role-policy already widens visible_sections for admin-tier
  // roles; this mirrors that on the persona side so the sidebar template
  // selection matches. Without this, an owner whose membership row drifted
  // to persona=contributor would render the contributor template even
  // though the server shipped the full admin section list.
  const effectivePersona =
    role && ADMIN_TIER_ROLES.has(role) ? PERSONAS.ADMIN : persona;

  return {
    role,
    persona,
    effectivePersona,
    visibleSections: Array.isArray(match.visible_sections)
      ? match.visible_sections
      : null,
    navigation: match.navigation ?? null,
    isPendingApproval:
      match.is_pending_approval === true ||
      (match.membership_status || '').toString().toLowerCase() === 'pending'
  };
};

const useSummaryRevision = (): number => {
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const bump = (event?: StorageEvent) => {
      if (!event || !event.key || event.key === 'user_summary') {
        setRevision((value) => value + 1);
      }
    };
    window.addEventListener('storage', bump);
    window.addEventListener('user-summary-updated', bump as EventListener);
    return () => {
      window.removeEventListener('storage', bump);
      window.removeEventListener('user-summary-updated', bump as EventListener);
    };
  }, []);

  return revision;
};

export const useWorkspaceVisibility = (
  workspaceIdOverride?: string | null
): WorkspaceVisibility => {
  const active = useActiveWorkspace();
  const workspaceId =
    workspaceIdOverride !== undefined
      ? workspaceIdOverride
      : active.workspaceId;
  const revision = useSummaryRevision();

  return useMemo(() => {
    const summary = readStoredUserSummary<SummaryShape>();
    const resolved = resolveVisibility(summary, workspaceId ?? null);
    return {
      workspaceId: workspaceId ?? null,
      isLoading: workspaceId !== null && workspaceId !== undefined && !summary,
      ...resolved
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, revision]);
};
